import { useState, useEffect } from 'react';
import { api } from '../../context/AuthContext';
import { Eye, CheckCircle, XCircle, User, Shield, Lock, Unlock, Key, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [pendingVerifications, setPendingVerifications] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [searchQuery, setSearchQuery] = useState('');
  const [resetPasswordModal, setResetPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [sort, setSort] = useState('created_at');
  const [order, setOrder] = useState('DESC');
  const LIMIT = 50;

  useEffect(() => {
    loadUsers();
    loadPendingVerifications();
  }, []);

  const loadUsers = async () => {
    try {
      const { data } = await api.get('/admin/users?page=' + page + '&limit=' + LIMIT + '&sort=' + sort + '&order=' + order + (searchQuery ? '&search=' + searchQuery : ''));
      setUsers(data.users || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalUsers(data.pagination?.total || 0);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sort === column) {
      setOrder(order === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSort(column);
      setOrder('DESC');
    }
    setPage(1);
    loadUsers();
  };

  const SortIcon = ({ column }) => {
    if (sort !== column) return <ChevronsUpDown className="w-3 h-3 ml-1 inline" />;
    return order === 'ASC' ? <ChevronUp className="w-3 h-3 ml-1 inline" /> : <ChevronDown className="w-3 h-3 ml-1 inline" />;
  };

  const loadPendingVerifications = async () => {
    try {
      const { data } = await api.get('/admin/users/pending-verification');
      setPendingVerifications(data.users || []);
    } catch (err) {
      console.error(err);
    }
  };

  const updateRole = async (userId, role) => {
    try {
      await api.put('/admin/users/' + userId + '/update-role', { role: role });
      loadUsers();
    } catch (err) {
      alert('Error al actualizar rol');
    }
  };

  const toggleVerified = async (userId, verified) => {
    try {
      await api.put('/admin/users/' + userId + '/verify', { verified: !verified });
      loadUsers();
      loadPendingVerifications();
    } catch (err) {
      alert('Error al actualizar verificacion');
    }
  };

  const updateSubscription = async (userId, plan) => {
    try {
      await api.put('/admin/users/' + userId + '/subscription', { plan: plan });
      loadUsers();
      alert('Suscripcion actualizada a ' + plan);
    } catch (err) {
      alert('Error al actualizar suscripcion');
    }
  };

  const resetPassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      alert('La contrasena debe tener al menos 6 caracteres');
      return;
    }
    try {
      await api.put('/admin/users/' + userId + '/reset-password', { password: newPassword });
      alert('Contrasena actualizada exitosamente');
      setResetPasswordModal(null);
      setNewPassword('');
    } catch (err) {
      alert(err.response?.data?.error || 'Error al resetear contrasena');
    }
  };

  const toggleBlock = async (userId, currentlyBlocked, reason) => {
    if (!currentlyBlocked && !confirm('Bloquear a este usuario?')) return;
    if (currentlyBlocked && !confirm('Desbloquear a este usuario?')) return;
    
    try {
      await api.put('/admin/users/' + userId + '/block', { 
        blocked: !currentlyBlocked,
        reason: reason || 'Bloqueado por administrador'
      });
      loadUsers();
    } catch (err) {
      alert('Error al cambiar estado de bloqueo');
    }
  };

  const approveVerification = async (userId) => {
    try {
      const { data } = await api.put('/admin/users/' + userId + '/approve-verification', {});
      alert(data.message || 'Verificacion aprobada');
      loadUsers();
      loadPendingVerifications();
      setSelectedUser(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Error al aprobar verificacion');
    }
  };

  const rejectVerification = async (userId) => {
    if (!confirm('Rechazar esta verificacion? El usuario debera subir nuevos documentos.')) return;
    try {
      await api.put('/admin/users/' + userId + '/verify', { verified: false });
      alert('Verificacion rechazada');
      loadPendingVerifications();
      setSelectedUser(null);
    } catch (err) {
      alert('Error al rechazar verificacion');
    }
  };

  const approveUser = async (userId, role) => {
    try {
      await api.put('/admin/users/' + userId + '/approve', { role: role });
      loadUsers();
    } catch (err) {
      alert('Error al aprobar usuario');
    }
  };

  const rejectUser = async (userId) => {
    if (!confirm('Rechazar este usuario?')) return;
    try {
      await api.put('/admin/users/' + userId + '/reject', {});
      loadUsers();
    } catch (err) {
      alert('Error al rechazar usuario');
    }
  };

  const getStatusBadge = (status, isBlocked) => {
    if (isBlocked) return <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">Bloqueado</span>;
    switch (status) {
      case 'PENDING': return <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs font-bold">Pendiente</span>;
      case 'ACTIVE': return <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-bold">Activo</span>;
      case 'REJECTED': return <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold">Rechazado</span>;
      default: return <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">{status}</span>;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'ADMIN': return <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs font-bold">Admin</span>;
      case 'SELLER': return <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs font-bold">Vendedor</span>;
      case 'BUYER': return <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs font-bold">Comprador</span>;
      default: return <span className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">{role}</span>;
    }
  };

  const getSubscriptionBadge = (plan) => {
    switch (plan) {
      case 'PREMIUM': return <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-2 py-1 rounded text-xs font-bold">PREMIUM</span>;
      case 'PRO': return <span className="bg-blue-500 text-white px-2 py-1 rounded text-xs font-bold">PRO</span>;
      default: return <span className="bg-gray-200 text-gray-600 px-2 py-1 rounded text-xs font-bold">FREE</span>;
    }
  };

  if (loading) return <div className="text-center py-8">Cargando...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-2xl font-black mb-6">Gestion de Usuarios</h1>
      
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab('users')}
          className={'px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition ' + (activeTab === 'users' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}
        >
          <User className="w-4 h-4" />
          Todos los Usuarios
          <span className="bg-black/20 px-2 py-0.5 rounded-full text-xs">{users.length}</span>
        </button>
        <button
          onClick={() => setActiveTab('verifications')}
          className={'px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition ' + (activeTab === 'verifications' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-100')}
        >
          <Shield className="w-4 h-4" />
          Verificaciones Pendientes
          {pendingVerifications.length > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {pendingVerifications.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'users' ? (
        <>
          <div className="mb-4">
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setPage(1);
                loadUsers();
              }}
              className="w-full md:w-96 border rounded-lg px-4 py-2 focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-4 font-semibold cursor-pointer hover:bg-gray-100" onClick={() => handleSort('name')}>
                      Usuario <SortIcon column="name" />
                    </th>
                    <th className="text-left p-4 font-semibold cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>
                      Estado <SortIcon column="status" />
                    </th>
                    <th className="text-left p-4 font-semibold cursor-pointer hover:bg-gray-100" onClick={() => handleSort('role')}>
                      Rol <SortIcon column="role" />
                    </th>
                    <th className="text-left p-4 font-semibold cursor-pointer hover:bg-gray-100" onClick={() => handleSort('subscription_plan')}>
                      Plan <SortIcon column="subscription_plan" />
                    </th>
                    <th className="text-left p-4 font-semibold">Productos</th>
                    <th className="text-left p-4 font-semibold">Verificado</th>
                    <th className="text-left p-4 font-semibold">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {users.map(u => (
                    <tr key={u.id} className={'hover:bg-gray-50 ' + (u.is_blocked ? 'bg-red-50' : '')}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={'w-10 h-10 rounded-full flex items-center justify-center font-bold ' + (u.is_blocked ? 'bg-red-200 text-red-700' : 'bg-primary text-white')}>
                            {u.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium">{u.name}</p>
                            <p className="text-xs text-gray-500">{u.email}</p>
                            {u.is_blocked && u.blocked_reason && (
                              <p className="text-xs text-red-500 mt-1">Razon: {u.blocked_reason}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="p-4">{getStatusBadge(u.status, u.is_blocked)}</td>
                      <td className="p-4">
                        <select
                          value={u.role}
                          onChange={(e) => updateRole(u.id, e.target.value)}
                          className="border rounded-lg px-3 py-1.5 text-sm bg-white"
                        >
                          <option value="BUYER">Comprador</option>
                          <option value="SELLER">Vendedor</option>
                          <option value="ADMIN">Admin</option>
                        </select>
                      </td>
                      <td className="p-4">
                        <select
                          value={u.subscription_plan || 'FREE'}
                          onChange={(e) => updateSubscription(u.id, e.target.value)}
                          className={'border rounded-lg px-3 py-1.5 text-sm font-medium ' + (u.subscription_plan === 'PREMIUM' ? 'bg-gradient-to-r from-yellow-100 to-orange-100 border-orange-300' : u.subscription_plan === 'PRO' ? 'bg-blue-50 border-blue-300' : 'bg-gray-50')}
                        >
                          <option value="FREE">FREE</option>
                          <option value="PRO">PRO</option>
                          <option value="PREMIUM">PREMIUM</option>
                        </select>
                      </td>
                      <td className="p-4">
                        <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 w-fit">
                          {u.product_count || 0}
                        </span>
                      </td>
                      <td className="p-4">
                        {u.is_verified ? (
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 w-fit">
                            <CheckCircle className="w-3 h-3" /> Verificado
                          </span>
                        ) : (
                          <button
                            onClick={() => toggleVerified(u.id, u.is_verified)}
                            className="bg-gray-100 text-gray-500 px-2 py-1 rounded-full text-xs font-medium hover:bg-gray-200"
                          >
                            No verificado
                          </button>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => { setResetPasswordModal(u); setNewPassword(''); }}
                            className="px-3 py-1.5 bg-blue-500 text-white rounded-lg text-xs font-medium hover:bg-blue-600 flex items-center gap-1"
                            title="Cambiar contrasena"
                          >
                            <Key className="w-3 h-3" /> Pass
                          </button>
                          
                          {u.is_blocked ? (
                            <button
                              onClick={() => toggleBlock(u.id, true)}
                              className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600 flex items-center gap-1"
                              title="Desbloquear usuario"
                            >
                              <Unlock className="w-3 h-3" /> Desbloq.
                            </button>
                          ) : (
                            <button
                              onClick={() => toggleBlock(u.id, false)}
                              className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600 flex items-center gap-1"
                              title="Bloquear usuario"
                            >
                              <Lock className="w-3 h-3" /> Bloq.
                            </button>
                          )}
                          
                          {u.status === 'PENDING' && (
                            <>
                              <button
                                onClick={() => approveUser(u.id, 'BUYER')}
                                className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600"
                              >
                                Aprobar
                              </button>
                              <button
                                onClick={() => rejectUser(u.id)}
                                className="px-3 py-1.5 bg-red-500 text-white rounded-lg text-xs font-medium hover:bg-red-600"
                              >
                                Rechazar
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {users.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                No hay usuarios
              </div>
            )}
            
            {totalUsers > 0 && (
              <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Mostrando {users.length} de {totalUsers} usuarios
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setPage(1); loadUsers(); }}
                    disabled={page === 1}
                    className="px-3 py-1.5 rounded border bg-white text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    ««
                  </button>
                  <button
                    onClick={() => { setPage(Math.max(1, page - 1)); loadUsers(); }}
                    disabled={page === 1}
                    className="px-3 py-1.5 rounded border bg-white text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    «
                  </button>
                  <span className="px-3 py-1.5 text-sm">
                    Pagina {page} de {totalPages}
                  </span>
                  <button
                    onClick={() => { setPage(Math.min(totalPages, page + 1)); loadUsers(); }}
                    disabled={page >= totalPages}
                    className="px-3 py-1.5 rounded border bg-white text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    »
                  </button>
                  <button
                    onClick={() => { setPage(totalPages); loadUsers(); }}
                    disabled={page >= totalPages}
                    className="px-3 py-1.5 rounded border bg-white text-sm disabled:opacity-50 hover:bg-gray-50"
                  >
                    »»
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-6">
          {pendingVerifications.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">Todo al dia!</h3>
              <p className="text-gray-500">No hay verificaciones pendientes</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-6">
              {pendingVerifications.map(user => (
                <div key={user.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4 bg-yellow-50 border-b border-yellow-200">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center font-bold text-white">
                        {user.name?.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-bold">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                        <p className="text-xs text-gray-400">Solicitado: {new Date(user.updated_at).toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4">
                    <h4 className="font-semibold mb-3">Documentos de Identidad</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Frente de Cedula</p>
                        {user.id_front ? (
                          <a href={user.id_front} target="_blank" rel="noopener noreferrer" className="block">
                            <img 
                              src={user.id_front} 
                              alt="Frente cedula" 
                              className="w-full h-32 object-cover rounded-lg border hover:border-primary cursor-pointer"
                            />
                          </a>
                        ) : (
                          <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                            No disponible
                          </div>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 mb-2">Reverso de Cedula</p>
                        {user.id_back ? (
                          <a href={user.id_back} target="_blank" rel="noopener noreferrer" className="block">
                            <img 
                              src={user.id_back} 
                              alt="Reverso cedula" 
                              className="w-full h-32 object-cover rounded-lg border hover:border-primary cursor-pointer"
                            />
                          </a>
                        ) : (
                          <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                            No disponible
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={() => approveVerification(user.id)}
                        className="flex-1 bg-green-500 text-white py-2 rounded-lg font-medium hover:bg-green-600 flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Aprobar Verificacion
                      </button>
                      <button
                        onClick={() => rejectVerification(user.id)}
                        className="px-4 py-2 bg-red-100 text-red-600 rounded-lg font-medium hover:bg-red-200 flex items-center justify-center gap-2"
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {resetPasswordModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg flex items-center gap-2">
                <Key className="w-5 h-5" />
                Nueva Contrasena
              </h3>
              <button onClick={() => setResetPasswordModal(null)} className="text-gray-500 hover:text-gray-700 text-xl">X</button>
            </div>
            
            <p className="text-sm text-gray-600 mb-4">
              Usuario: <strong>{resetPasswordModal.name}</strong>
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">Nueva Contrasena</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full border rounded-lg px-4 py-3 focus:ring-2 focus:ring-primary"
                placeholder="Minimo 6 caracteres"
                minLength={6}
              />
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => resetPassword(resetPasswordModal.id)}
                className="flex-1 bg-blue-500 text-white py-3 rounded-lg font-medium hover:bg-blue-600 flex items-center justify-center gap-2"
              >
                <Key className="w-4 h-4" />
                Cambiar Contrasena
              </button>
              <button
                onClick={() => setResetPasswordModal(null)}
                className="px-6 py-3 bg-gray-100 text-gray-600 rounded-lg font-medium hover:bg-gray-200"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-lg">Verificacion de: {selectedUser.name}</h3>
              <button onClick={() => setSelectedUser(null)} className="text-gray-500 hover:text-gray-700">X</button>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="font-medium mb-2">Frente de Cedula</p>
                  {selectedUser.id_front ? (
                    <a href={selectedUser.id_front} target="_blank" rel="noopener noreferrer">
                      <img src={selectedUser.id_front} alt="Frente" className="w-full rounded-lg border hover:border-primary" />
                    </a>
                  ) : (
                    <div className="bg-gray-100 p-8 text-center rounded-lg text-gray-400">No disponible</div>
                  )}
                </div>
                <div>
                  <p className="font-medium mb-2">Reverso de Cedula</p>
                  {selectedUser.id_back ? (
                    <a href={selectedUser.id_back} target="_blank" rel="noopener noreferrer">
                      <img src={selectedUser.id_back} alt="Reverso" className="w-full rounded-lg border hover:border-primary" />
                    </a>
                  ) : (
                    <div className="bg-gray-100 p-8 text-center rounded-lg text-gray-400">No disponible</div>
                  )}
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => { approveVerification(selectedUser.id); setSelectedUser(null); }}
                  className="flex-1 bg-green-500 text-white py-3 rounded-lg font-medium hover:bg-green-600"
                >
                  Aprobar Verificacion
                </button>
                <button
                  onClick={() => { rejectVerification(selectedUser.id); setSelectedUser(null); }}
                  className="px-6 py-3 bg-red-100 text-red-600 rounded-lg font-medium hover:bg-red-200"
                >
                  Rechazar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
